import { useMemo, useRef, useState } from 'react';
import {
  processImageContentForSave,
  type LibraryImage,
} from 'components/DesignSystems/New/TiptapRichTextEditor';
import {
  SitrepSectionType,
  useUpdateSitrepSectionV2,
  isSitrepSectionConflictError,
  type SitrepSectionConflictDetails,
} from 'components/DisasterGPT/hooks/useSitrepsV2';
import Sentry from '../../../instrument';
import { useSitrepSectionYjs } from '../collab/useSitrepSectionYjs';
import { sitrepSectionDisplay } from './sitrepSectionDisplay';

type UseSectionContentEditorParams = {
  section: SitrepSectionType;
  sitrepId: string;
  sitrepImages?: LibraryImage[];
  canEdit: boolean;
  onUpdated?: () => void;
  // Skip the success toast on save (document view shows its own indicator).
  silent?: boolean;
  /** Enable live Yjs collab while editing this TipTap section. */
  enableCollab?: boolean;
  /** When true (AI lock), collab room stays read-only / unmounted for edits. */
  isAiLocked?: boolean;
};

// Per-section edit/save/conflict state machine shared by the tabbed section
// display (ReportsSectionDisplay) and the continuous document view.
//
// Concurrency model: with collab enabled, peers sync via Yjs while editing.
// REST persist (updateSitrepSection) happens only on explicit Save. Without
// collab (or on transport failure), baseVersionId + 409 conflict modal remain
// the fallback.
export const useSectionContentEditor = ({
  section,
  sitrepId,
  sitrepImages,
  canEdit,
  onUpdated,
  silent,
  enableCollab = false,
  isAiLocked = false,
}: UseSectionContentEditorParams) => {
  const [editingContent, setEditingContent] = useState(false);
  const [content, setContent] = useState('');
  const [baseVersionId, setBaseVersionId] = useState<string | undefined>(
    undefined
  );
  const [conflict, setConflict] = useState<SitrepSectionConflictDetails | null>(
    null
  );
  const contentRef = useRef(content);
  contentRef.current = content;

  const updateSectionMutation = useUpdateSitrepSectionV2();

  const latestVersion = useMemo(
    () => sitrepSectionDisplay(section).display ?? section.section_versions?.[0],
    [section]
  );

  const pendingReview = useMemo(
    () => sitrepSectionDisplay(section).pending,
    [section]
  );

  const sectionText = useMemo(
    () => latestVersion?.sitrep_section_text || '',
    [latestVersion?.sitrep_section_text]
  );

  const sectionStatus = useMemo(
    () => section.section_versions?.[0]?.status?.toLowerCase() || '',
    [section.section_versions]
  );

  const isProcessing = useMemo(
    () => ['not started', 'fetching', 'uploading'].includes(sectionStatus),
    [sectionStatus]
  );

  const enableEditing =
    section.type !== 'automated' &&
    !(section.type === 'ai' && isProcessing) &&
    !pendingReview &&
    !isAiLocked;
  const canInteract = canEdit && enableEditing;
  const isEditing = editingContent && canEdit && !isAiLocked;
  const isSaving = updateSectionMutation.isPending;

  const collabEnabled =
    enableCollab && isEditing && !isAiLocked && section.type !== 'automated';

  const { ydoc } = useSitrepSectionYjs({
    sitrepId,
    sectionId: section.id,
    enabled: collabEnabled,
  });

  const saveContent = async (
    overrideBaseVersionId?: string,
    overrideContent?: string
  ) => {
    const chosenContent = overrideContent ?? contentRef.current;

    const contentWithFileImages = processImageContentForSave(
      chosenContent,
      sitrepImages || []
    );

    try {
      await updateSectionMutation.mutateAsync({
        sitrepId,
        sectionId: section.id,
        baseVersionId: overrideBaseVersionId ?? baseVersionId,
        silent,
        updatedFields: { sectionText: contentWithFileImages },
      });
      setContent(sectionText);
      setEditingContent(false);
      setBaseVersionId(undefined);
      setConflict(null);
      onUpdated?.();
    } catch (error) {
      if (isSitrepSectionConflictError(error)) {
        setConflict(error.conflict);
        return;
      }
      Sentry.captureException(error);
    }
  };

  const onContentChange = (html: string) => {
    setContent(html);
    contentRef.current = html;
  };

  const overrideConflict = (latestVersionId: string, editedContent: string) => {
    setConflict(null);
    saveContent(latestVersionId, editedContent);
  };

  const keepExisting = () => {
    setConflict(null);
    setEditingContent(false);
    setBaseVersionId(undefined);
    onUpdated?.();
  };

  const startEditing = () => {
    if (!canEdit || !enableEditing) {
      return;
    }
    setContent(sectionText);
    contentRef.current = sectionText;
    setBaseVersionId(latestVersion?.version_id);
    setEditingContent(true);
  };

  const cancelEditing = () => {
    setEditingContent(false);
    setBaseVersionId(undefined);
  };

  return {
    isEditing,
    content,
    setContent,
    conflict,
    isSaving,
    latestVersion,
    pendingReview,
    sectionText,
    sectionStatus,
    isProcessing,
    enableEditing,
    canInteract,
    ydoc,
    collabEnabled,
    startEditing,
    cancelEditing,
    save: () => saveContent(),
    onContentChange,
    overrideConflict,
    keepExisting,
  };
};
